import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Check, Zap, Clock, Circle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_BADGE = {
  Sales: { bg: "#EEF0FD", color: "#3B4FE4" },
  Operations: { bg: "#E8EAF4", color: "#3730A3" },
  Finance: { bg: "#E6F5ED", color: "#1A7A4A" },
  HR: { bg: "#FDF4FF", color: "#7E22CE" },
  Admin: { bg: "#FFF4E0", color: "#D4860A" },
  "Customer Service": { bg: "#EEF0FD", color: "#3B4FE4" },
  Custom: { bg: "#FFF4E0", color: "#D4860A" },
  Other: { bg: "#FFF4E0", color: "#D4860A" },
};

const RISK_COLORS = {
  Low: "#1A7A4A",
  Medium: "#D4860A",
  High: "#C0392B",
};

const PRIORITY_BADGE_COLORS = {
  1: { bg: "#3B4FE4", color: "white" },
  2: { bg: "#FFF4E0", color: "#D4860A" },
  3: { bg: "#E6F5ED", color: "#1A7A4A" },
  default: { bg: "#EEEEF5", color: "#4B5563" },
};

const MAX_SELECTIONS = 5;

const fakeId = () => `wf_${Math.random().toString(36).slice(2, 10)}`;

const normalizeWorkflows = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((w, idx) => ({
    id: w.id || w.workflow_id || `wf_${idx + 1}`,
    name: w.workflow_name || w.name || w.title || `Workflow ${idx + 1}`,
    category: w.department || w.category || w.business_area || "Operations",
    description: w.why_ai_fits || w.description || w.summary || w.relevance_to_answers || "",
    pain_point: Array.isArray(w.main_pain_points) ? w.main_pain_points.join(" · ") : (w.pain_point || w.pain_points || ""),
    expected_outcome: w.kpi_target || w.expected_outcome || w.outcome || "",
    speed: w.speed || (String(w.delivery_method || "").includes("Ready") ? "Fast" : "Medium"),
    risk_level: w.risk_level || w.risk || "Low",
    timeline_weeks: w.timeline_weeks ?? w.timeline ?? 2,
    ai_priority: w.ai_priority || w.priority || w.recommended_rank || (idx + 1),
    is_backup: w.is_backup === true,
    source: w.source || (w.is_backup ? "backup" : "recommended"),
  }));
};

export default function WorkflowSelection({ tenantId, prospectId, recommendedWorkflows, preSelectedIds, onConfirmed, isLocked = false }) {
  const [workflows, setWorkflows] = useState(() => normalizeWorkflows(recommendedWorkflows));
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customCategory, setCustomCategory] = useState("Sales");
  const [customDesc, setCustomDesc] = useState("");

  // Re-normalize when the upstream list changes (e.g., portal refresh after assessment).
  useEffect(() => {
    setWorkflows(normalizeWorkflows(recommendedWorkflows));
  }, [recommendedWorkflows]);

  // Pre-select: prefer explicit preSelectedIds, fall back to top-3 by ai_priority.
  useEffect(() => {
    if (workflows.length > 0 && selectedIds.length === 0) {
      const explicit = (preSelectedIds || []).filter((id) => workflows.some((w) => w.id === id));
      if (explicit.length > 0) {
        setSelectedIds(explicit.slice(0, MAX_SELECTIONS));
        return;
      }
      const top3 = [...workflows]
        .sort((a, b) => (a.ai_priority || 99) - (b.ai_priority || 99))
        .slice(0, MAX_SELECTIONS)
        .map((w) => w.id);
      setSelectedIds(top3);
    }
  }, [workflows, preSelectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_SELECTIONS) {
        toast.error(`You can only select up to ${MAX_SELECTIONS} workflows. Deselect one first.`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const selectedWorkflows = useMemo(
    () => workflows.filter((w) => selectedIds.includes(w.id)),
    [workflows, selectedIds],
  );

  const handleAddCustom = () => {
    if (!customName.trim()) {
      toast.error("Workflow name is required");
      return;
    }
    const newWf = {
      id: fakeId(),
      name: customName.trim(),
      category: customCategory || "Custom",
      description: customDesc.trim() || `Custom workflow: ${customName.trim()}`,
      pain_point: "Defined by client",
      expected_outcome: "To be measured",
      speed: "Medium",
      risk_level: "Low",
      timeline_weeks: 3,
      ai_priority: workflows.length + 1,
      source: "custom",
    };
    setWorkflows((prev) => [...prev, newWf]);
    if (selectedIds.length < MAX_SELECTIONS) {
      setSelectedIds((prev) => [...prev, newWf.id]);
    }
    setCustomName("");
    setCustomCategory("Sales");
    setCustomDesc("");
    setShowCustomForm(false);
    toast.success("Custom workflow added");
  };

  const handleConfirm = async () => {
    if (selectedWorkflows.length < 1) return;
    setConfirming(true);
    try {
      const finalSelection = selectedWorkflows.map((w, idx) => ({
        ...w,
        priority: idx + 1,
        status: "not_started",
        playbook_id: null,
      }));
      const res = await fetch(`${BACKEND_URL}/api/playbooks/confirm-workflows`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": tenantId || "tenant_1",
          "x-user-id": "client",
          "x-user-role": "client",
        },
        body: JSON.stringify({
          confirmed_workflows: finalSelection,
          prospect_id: prospectId,
        }),
      });
      if (!res.ok) throw new Error((await res.json())?.detail || "Confirm failed");
      const data = await res.json();
      toast.success(`${data.playbooks_created || 0} Playbooks ready to configure`);
      if (onConfirmed) onConfirmed(finalSelection);
    } catch (e) {
      toast.error("Could not confirm selection", { description: e?.message });
    } finally {
      setConfirming(false);
    }
  };

  const allLocked = selectedIds.length >= MAX_SELECTIONS;
  const counterDots = Array.from({ length: MAX_SELECTIONS }, (_, i) => i);

  return (
    <div className="w-full pb-32" data-testid="workflow-selection-screen">
      {/* FIX-04c (May-2026): once scope is confirmed/SOW exists, lock the
          page. Selections become read-only and the customer sees a clear banner. */}
      {isLocked && (
        <div
          className="mb-5 rounded-xl border border-[#3B4FE4]/30 bg-[#EEF0FD] px-5 py-4 flex items-start gap-3"
          data-testid="workflow-locked-banner"
        >
          <Check className="h-5 w-5 text-[#3B4FE4] mt-0.5 flex-shrink-0" strokeWidth={3} />
          <div>
            <div className="text-[14px] font-semibold text-[#1F2A87]">Your AI Workflow Plan is confirmed</div>
            <div className="text-[12px] text-[#4B5563] mt-1 leading-relaxed">
              These workflows are now in motion through your Scope of Services and Roadmap.
              Need to swap or add a workflow? Message your 589 Labs consultant from the Comments tab.
            </div>
          </div>
        </div>
      )}
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold tracking-tight text-[#111827]">Select Your Starting Workflows</h1>
        <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">
          Based on your assessment, we identified <strong>{workflows.length} high-impact opportunities</strong> below.
          Choose <strong>up to 5</strong> to begin with — these will become your first AI implementation projects. You
          can swap or add more after your first cycle.
        </p>
      </div>

      {/* Selection counter bar */}
      <div className="rounded-xl border border-[rgba(0,0,0,0.08)] bg-white p-4 flex items-center justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-2">
          {counterDots.map((i) => {
            const filled = selectedIds.length > i;
            return (
              <div
                key={i}
                className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold transition-all"
                style={
                  filled
                    ? { backgroundColor: "#3B4FE4", color: "white" }
                    : { backgroundColor: "#EEEEF5", color: "#9CA3AF" }
                }
              >
                {filled ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
              </div>
            );
          })}
          <div className="ml-3 text-[14px] text-[#111827]">
            <strong>{selectedIds.length} of {MAX_SELECTIONS}</strong> workflows selected
          </div>
        </div>
        <span className="text-[12px] text-[#9CA3AF]">
          {selectedIds.length < 1
            ? "Select at least 1 to continue"
            : selectedIds.length < MAX_SELECTIONS
              ? `Select ${MAX_SELECTIONS - selectedIds.length} more to unlock Scope of Services`
              : "Ready to proceed"}
        </span>
      </div>

      {/* Recommended divider */}
      <div className="flex items-center gap-3 mb-3 text-[12px]">
        <span className="text-[#3B4FE4] font-bold uppercase tracking-[0.10em]">Recommended by AI assessment</span>
        <div className="flex-1 h-px bg-[rgba(0,0,0,0.08)]" />
        <span className="text-[#9CA3AF]">{workflows.length} opportunities identified</span>
      </div>

      {/* Workflow rows */}
      <div className="space-y-2.5">
        {workflows.map((w) => {
          const selected = selectedIds.includes(w.id);
          const dimmed = isLocked || (!selected && allLocked);
          const cat = CATEGORY_BADGE[w.category] || CATEGORY_BADGE.Custom;
          const priority = w.ai_priority || 99;
          const pBadge = PRIORITY_BADGE_COLORS[priority] || PRIORITY_BADGE_COLORS.default;
          const riskColor = RISK_COLORS[w.risk_level] || "#9CA3AF";
          return (
            <button
              key={w.id}
              type="button"
              onClick={() => !dimmed && toggleSelect(w.id)}
              disabled={dimmed}
              className="w-full text-left rounded-xl border bg-white p-5 transition-all hover:bg-[#fafbff]"
              style={{
                borderColor: selected ? "#3B4FE4" : "rgba(0,0,0,0.08)",
                borderWidth: selected ? "2px" : "1px",
                boxShadow: selected ? "0 0 0 4px rgba(59,79,228,0.10)" : "none",
                opacity: dimmed ? 0.5 : 1,
                cursor: dimmed ? "not-allowed" : "pointer",
              }}
              data-testid={`workflow-row-${w.id}`}
            >
              <div className="flex items-start gap-4">
                {/* Selection circle */}
                <div
                  className="mt-0.5 h-6 w-6 rounded-full shrink-0 flex items-center justify-center"
                  style={
                    selected
                      ? { backgroundColor: "#3B4FE4", color: "white" }
                      : { border: "2px solid #d1d5db", backgroundColor: "white" }
                  }
                >
                  {selected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[16px] font-semibold text-[#111827]">{w.name}</div>
                      <span
                        className="px-2 py-0.5 text-[11px] font-semibold rounded-full"
                        style={{ backgroundColor: cat.bg, color: cat.color }}
                      >
                        {w.category}
                      </span>
                      {w.source === "custom" && (
                        <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full" style={{ backgroundColor: "#FFF4E0", color: "#D4860A" }}>
                          Custom
                        </span>
                      )}
                    </div>
                    <span
                      className="px-2 py-0.5 text-[11px] font-bold rounded"
                      style={{ backgroundColor: pBadge.bg, color: pBadge.color }}
                    >
                      Priority {priority}
                    </span>
                  </div>
                  <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{w.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {w.pain_point && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full bg-[#FFF4E0] text-[#92400E]">
                        ⚠ {w.pain_point}
                      </span>
                    )}
                    {w.expected_outcome && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full bg-[#E6F5ED] text-[#166534]">
                        ✓ {w.expected_outcome}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-[#4B5563]">
                    <span className="inline-flex items-center gap-1.5">
                      <Zap className="h-3.5 w-3.5 text-[#D4860A]" />
                      Speed: <strong className="text-[#111827]">{w.speed}</strong>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Circle className="h-3 w-3" style={{ fill: riskColor, color: riskColor }} />
                      Risk: <strong style={{ color: riskColor }}>{w.risk_level}</strong>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-[#3B4FE4]" />
                      Timeline: <strong className="text-[#111827]">{w.timeline_weeks} weeks</strong>
                    </span>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom workflow */}
      <div className="mt-8">
        <div className="flex items-center gap-3 mb-3 text-[12px]">
          <span className="text-[#9CA3AF] font-semibold uppercase tracking-[0.10em]">Don't see what you need?</span>
          <div className="flex-1 h-px bg-[rgba(0,0,0,0.08)]" />
        </div>
        {!showCustomForm ? (
          <button
            type="button"
            onClick={() => setShowCustomForm(true)}
            className="w-full rounded-xl border border-dashed border-[#d1d5db] bg-white p-5 text-[14px] font-semibold text-[#4B5563] hover:border-[#3B4FE4] hover:text-[#3B4FE4] hover:bg-[#fafbff] transition-colors flex items-center justify-center gap-2"
            data-testid="add-custom-workflow-btn"
          >
            <Plus className="h-4 w-4" /> Add a custom workflow not on this list
          </button>
        ) : (
          <div className="rounded-xl border border-[rgba(0,0,0,0.08)] bg-white p-5 space-y-3" data-testid="custom-workflow-form">
            <div className="text-[15px] font-semibold text-[#111827]">Add your own workflow</div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">Workflow name *</label>
              <input
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 focus:border-[#3B4FE4] focus:outline-none"
                placeholder="e.g. Vendor PO Approval Routing"
                data-testid="custom-name-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Business area</label>
                <select
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  className="w-full h-10 rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-2 focus:border-[#3B4FE4] focus:outline-none"
                  data-testid="custom-category-select"
                >
                  {Object.keys(CATEGORY_BADGE).filter((k) => k !== "Custom").map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">Describe the problem this workflow solves</label>
              <textarea
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-[rgba(0,0,0,0.15)] bg-white text-[14px] px-3 py-2 focus:border-[#3B4FE4] focus:outline-none resize-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddCustom}
                className="px-4 h-9 rounded-lg text-[13px] font-semibold text-white"
                style={{ backgroundColor: "#3B4FE4" }}
                data-testid="custom-submit-btn"
              >
                Add to my list
              </button>
              <button
                type="button"
                onClick={() => setShowCustomForm(false)}
                className="px-4 h-9 rounded-lg text-[13px] font-semibold text-[#4B5563] hover:bg-[#f3f4f6]"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky confirm footer — hidden when scope is already confirmed (FIX-04c). */}
      {!isLocked && (
      <div className="fixed bottom-0 left-0 right-0 lg:left-[228px] z-30 border-t border-[rgba(0,0,0,0.08)] bg-white px-6 py-4">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13px] text-[#4B5563] min-w-0">
            <strong className="text-[#111827]">{selectedWorkflows.length} workflow{selectedWorkflows.length !== 1 ? "s" : ""} selected:</strong>
            <span className="ml-2 truncate">
              {selectedWorkflows.map((w) => w.name).join(", ") || "None"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-4 h-10 rounded-lg text-[13px] font-semibold text-[#4B5563] hover:bg-[#f3f4f6]"
              data-testid="workflow-back-btn"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedWorkflows.length < 1 || confirming}
              className="px-5 h-10 rounded-lg text-[13px] font-bold text-white disabled:opacity-40 transition-opacity flex items-center gap-2"
              style={{ backgroundColor: "#3B4FE4", boxShadow: "0 4px 12px rgba(59,79,228,0.30)" }}
              data-testid="workflow-confirm-btn"
            >
              {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirm Selection & Proceed to Scope →
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
